import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Field } from 'factorial-form'
import { ShiftManagementRolesRequirementsFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import i18n from 'modules/core/lib/i18n'

import { LocationForm } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/LocationForm'
import { StaffRequirements } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/StaffRequirements'
import getFilterValue from 'modules/shift_management/components/ShiftManagement/Content/_filters/getFilterValue'
import RangeDatePicker from 'modules/shift_management/components/ShiftManagement/RangeDatePicker'
import { SHIFT_MANAGEMENT_FILTERS } from 'modules/shift_management/hooks/reducers/filter'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

import { ShiftType, ShiftTypeOptions } from '../utils'
import NewDefaultShiftForm from './NewDefaultShiftForm'
import NewShiftForm from './NewShiftForm'

type StaffRequirement = {
  job_catalog_roles_ids: number[]
  staff_count: number
}

interface Props {
  unassignedDefaultShiftsForms: UnassignedShiftForm[]
  unassignedShiftForm: UnassignedShiftForm
  onToggleShift: (id: string) => void
  onSelectShiftType: (type: ShiftType) => void
  shiftType: ShiftType
  roles: ShiftManagementRolesRequirementsFragment[]
}

const Content = ({
  unassignedDefaultShiftsForms,
  unassignedShiftForm,
  onToggleShift,
  onSelectShiftType,
  shiftType,
  roles,
}: Props) => {
  const [workAreaFilterValue] = useState(new Field('', 'string'))
  const { filters } = useEmployeeMultiFilters(getFilterValue, LOCAL_STORAGE_KEYS.shiftEmployees)
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureFlag(
    ShiftManagementFeatureFlags.DEV_SHIFT_MANAGEMENT_AUTOMATION
  )
  const handleAddStaffRequirement = () => {
    unassignedShiftForm.setValues({
      staff_requirements: [
        ...unassignedShiftForm.get('staff_requirements').value,
        { job_catalog_roles_ids: [], staff_count: 1 },
      ],
    })
  }

  const locationFilter = filters.filter(
    (f) => f.values.field === SHIFT_MANAGEMENT_FILTERS.employeeLocation
  )
  const locationFilterValue =
    locationFilter.length === 1
      ? new Field(locationFilter.at(0)?.values?.value, 'string')
      : new Field('', 'string')

  return (
    <Box flexDirection="column" gap="s24">
      {isAutomationEnabled && (
        <LocationForm
          locationFilterValue={locationFilterValue}
          workAreaFilterValue={workAreaFilterValue}
        />
      )}
      <Box gap="s8">
        <Box flexDirection="row" gap="s4" alignItems="center">
          <Text.Normal size="200" color="grey1000" weight="medium">
            {i18n.t('shift_management.coverage_section.unassigned_shift.date_range')}
          </Text.Normal>
        </Box>
        <RangeDatePicker
          startDate={unassignedShiftForm.get('start_date')}
          endDate={unassignedShiftForm.get('end_date')}
          hideLabel
        />
      </Box>

      <Box gap="s8">
        <Box flexDirection="row" gap="s4" alignItems="center">
          <Text.Normal size="300" color="grey1000" weight="medium">
            {i18n.t('shift_management.coverage_section.unassigned_shift.staff_requirements.title')}
          </Text.Normal>
        </Box>

        {unassignedShiftForm
          .get('staff_requirements')
          .value.map((_: StaffRequirement, i: number) => (
            <StaffRequirements key={i} form={unassignedShiftForm} id={i} roles={roles} />
          ))}
        <Box width="s32" height="s32">
          <Button type="secondary" onClick={handleAddStaffRequirement} iconName="add" />
        </Box>
      </Box>

      <Box gap="s8">
        <NewShiftForm
          form={unassignedShiftForm}
          onSelectNewFromScratch={() => onSelectShiftType(ShiftTypeOptions.custom)}
          isSelected={shiftType === ShiftTypeOptions.custom}
          fromUnassigned
          isAutomationEnabled={isAutomationEnabled}
          locationFilterValue={locationFilterValue.value}
          workAreaFilterValue={workAreaFilterValue.value}
        />

        <NewDefaultShiftForm
          forms={unassignedDefaultShiftsForms}
          onToggleShift={onToggleShift}
          isSelected={shiftType === ShiftTypeOptions.default}
          onSelectDefaultShiftType={() => onSelectShiftType(ShiftTypeOptions.default)}
          isAutomationEnabled={isAutomationEnabled}
          filters={filters}
          locationFilterValue={locationFilterValue.value}
          workAreaFilterValue={workAreaFilterValue.value}
        />
      </Box>
    </Box>
  )
}

export default observer(Content)
